import CmsImage from '@/components/ui/CmsImage';
import { PlantsHeroProps } from '@/types/plants';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import { getStrapiOriginalMediaUrl, isStrapiVideoMedia, isVideoUrl } from '@/lib/strapi-media-url';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

const DEFAULT_HERO_IMAGE = '/images/plant_hr.jpg';

const HERO_DESCRIPTION_FALLBACK =
  "At Wonder Cement, scale is a capability and precision is a choice. Our manufacturing ecosystem, anchored by one of India's largest single-location integrated plants and a high-speed grinding network, is engineered for the 21st century.";

export default function PlantsHero({ hero }: PlantsHeroProps) {
  const heroMediaUrl = getStrapiOriginalMediaUrl(hero.backgroundImage) || DEFAULT_HERO_IMAGE;
  const isHeroVideo = isStrapiVideoMedia(hero.backgroundImage) || isVideoUrl(heroMediaUrl);
  const descriptions =
    hero.descriptions.length > 0 ? hero.descriptions : [HERO_DESCRIPTION_FALLBACK];

  return (
    <section className="relative w-full overflow-hidden bg-black text-white">
      {/* Mobile / Tablet: full-width image, natural height; content follows below */}
      <div className="flex flex-col bg-black lg:hidden">
        <div className="relative w-full shrink-0">
          <CmsImage
            src={heroMediaUrl}
            media={isHeroVideo ? hero.backgroundImage : null}
            alt={hero.title}
            width={1920}
            height={1080}
            priority
            loading="eager"
            className="h-auto w-full object-cover"
            sizes="100vw"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
        </div>
        <div className="px-4 py-10 sm:px-6 sm:py-12 md:px-8">
          <div className="mx-auto max-w-[640px]">
            <CmsRichTextHeading
              className="hero-title text-white"
              html={hero.titleRichText}
              fallback={hero.title}
            />
            {hero.subtitle ? (
              <p className="hero-subtitle mt-4 leading-snug text-white/95 sm:mt-5">
                {hero.subtitle}
              </p>
            ) : null}
            <div className="mt-4 space-y-4 sm:mt-5">
              {descriptions.map((description) => (
                <p key={description} className="hero-subtitle text-white/90">
                  {description}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: full-bleed overlay hero */}
      <section className="relative hidden min-h-screen w-full overflow-hidden bg-black lg:block">
        <div className="absolute inset-0">
          <CmsImage
            src={heroMediaUrl}
            media={isHeroVideo ? hero.backgroundImage : null}
            alt={hero.title}
            fill
            priority
            loading="eager"
            className="object-cover object-top"
            sizes="100vw"
            posterSrc={MEDIA_PLACEHOLDER}
            fallbackSrc={MEDIA_PLACEHOLDER}
          />
          <div className="pointer-events-none absolute inset-0 z-30 bg-black/10" />
          <div
            className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0.55)_22%,rgba(0,0,0,0.2)_42%,transparent_72%)]"
            aria-hidden
          />
        </div>

        <div className="relative z-10 flex min-h-screen flex-col justify-end">
          <div className="container mx-auto py-12 md:py-14 lg:py-16">
            <div className="max-w-[660px]">
              <CmsRichTextHeading
                className="hero-title text-white"
                html={hero.titleRichText}
                fallback={hero.title}
              />
              {hero.subtitle ? (
                <p className="hero-subtitle mt-5 leading-snug text-white/95 lg:mt-6">
                  {hero.subtitle}
                </p>
              ) : null}
              <div className="mt-5 space-y-4 lg:mt-6">
                {descriptions.map((description) => (
                  <p key={description} className="hero-subtitle text-white/90">
                    {description}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}
